import { useBlocker } from "@remix-run/react";
import { CreateOrderInputSchema, ExpirationOptions, type ExpirationType } from "@repo/api/src/schemas/order.schema";
import type { CameraPose, CenterMode } from "@workspace/blocks";
import { DEFAULT_CENTER_MODE } from "@workspace/blocks";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { trpc } from "../../lib/trpc";
import { useUploadDropReceiver } from "../components/upload/GlobalUploadDrop";
import { useTranslation } from "../i18n";
import { buildUploadMetadata, encodeDescriptionWithMetadata } from "../utils/splatMetadata";
import { uploadFileError } from "../utils/uploadFile";
import { type UploadFailureCategory, uploadFailureCategory, useChunkedUpload } from "./useChunkedUpload";

export interface UploadSettings {
	title: string;
	description: string;
	expirationType: ExpirationType;
	hasPassword: boolean;
	password: string;
	customCameraPose?: CameraPose | null;
	centerMode: CenterMode;
}

export function useIndexPageContext() {
	const { t } = useTranslation();
	const createOrder = trpc.order.createOrder.useMutation({ networkMode: "always" });
	const {
		uploadFile: uploadFileChunked,
		cancelUpload,
		reset,
		setStage,
		progress: uploadProgress,
		isUploading: isChunkedUploading,
	} = useChunkedUpload();

	const [file, setFile] = useState<File | null>(null);
	const [shortUrl, setShortUrl] = useState<string | null>(null);
	const [uploading, setUploading] = useState<boolean>(false);
	const [uploadError, setUploadError] = useState<
		{ message: string; recovery?: string; code?: string; tone?: "error" | "info" } | undefined
	>();
	const cancelledRef = useRef(false);
	const uploadedFileRef = useRef<{ file: File; url: string } | null>(null);
	const [selectionError, setSelectionError] = useState<string | null>(null);
	const [offline, setOffline] = useState(false);
	const [discardPending, setDiscardPending] = useState(false);
	const pendingAction = useRef<(() => void) | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Settings stay with the selected file until upload succeeds or the draft is discarded.
	const [settings, setSettings] = useState<UploadSettings>({
		title: "",
		description: "",
		expirationType: ExpirationOptions.WEEK,
		hasPassword: false,
		password: "",
		customCameraPose: null,
		centerMode: DEFAULT_CENTER_MODE,
	});

	const handleFileSelect = useCallback(
		(selectedFile: File) => {
			const error = uploadFileError(selectedFile);
			if (error) {
				setSelectionError(t(error));
				return;
			}
			setSelectionError(null);
			setShortUrl(null);
			// Use the filename without extension as the initial title.
			const title = selectedFile.name.replace(/\.[^/.]+$/, "").slice(0, 100);
			reset();
			uploadedFileRef.current = null;
			setUploadError(undefined);

			// Configure the selected file in the workspace.
			setSettings(prev => ({
				...prev,

				title: title,
				description: "", // Start with an empty description
				expirationType: ExpirationOptions.WEEK,
				hasPassword: false,
				password: "",
				customCameraPose: null,
				centerMode: DEFAULT_CENTER_MODE,
			}));

			// Preserve the original filename for storage.
			setFile(selectedFile);
		},
		[t, reset],
	);

	const defaultTitle = file?.name.replace(/\.[^/.]+$/, "").slice(0, 100) || "";
	const dirty =
		!!file &&
		!shortUrl &&
		(settings.title !== defaultTitle ||
			!!settings.description ||
			settings.hasPassword ||
			!!settings.customCameraPose ||
			settings.centerMode !== DEFAULT_CENTER_MODE ||
			settings.expirationType !== ExpirationOptions.WEEK);
	const shouldBlock = dirty || uploading;
	const blocker = useBlocker(shouldBlock);
	const requestAction = (action: () => void) => {
		if (shouldBlock) {
			pendingAction.current = action;
			setDiscardPending(true);
		} else action();
	};
	const selectFile = (selected: File) => requestAction(() => handleFileSelect(selected));
	useUploadDropReceiver(selectFile, uploading || isChunkedUploading);
	const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const selected = event.target.files?.[0];
		event.target.value = "";
		if (selected) selectFile(selected);
	};
	useEffect(() => {
		const sync = () => setOffline(!navigator.onLine);
		sync();
		window.addEventListener("online", sync);
		window.addEventListener("offline", sync);
		return () => {
			window.removeEventListener("online", sync);
			window.removeEventListener("offline", sync);
		};
	}, []);
	useEffect(() => {
		if (!shouldBlock) return;
		const prevent = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", prevent);
		return () => window.removeEventListener("beforeunload", prevent);
	}, [shouldBlock]);

	const openFileDialog = () => {
		fileInputRef.current?.click();
	};

	const showUploadFailure = (category: UploadFailureCategory, status?: number) => {
		const keys = {
			offline: "Offline",
			network: "Network",
			timeout: "Timeout",
			server: "Server",
			busy: "Busy",
			file: "File",
			permission: "Permission",
			unknown: "Unknown",
		} as const;
		const key = keys[category];
		setUploadError({
			message: t(`uploadModal.error${key}`),
			recovery: t(`uploadModal.recovery${key}`),
			code: status ? `HTTP ${status}` : undefined,
		});
	};
	const metadata = buildUploadMetadata(settings.customCameraPose, settings.centerMode);
	const finalDescription = encodeDescriptionWithMetadata(settings.description, metadata);
	const descriptionLimit = Math.max(0, 1000 - encodeDescriptionWithMetadata("", metadata).length);
	const validation = CreateOrderInputSchema.safeParse({
		title: settings.title.trim(),
		description: finalDescription,
		expirationType: settings.expirationType,
		password: settings.hasPassword ? settings.password : undefined,
	});
	const hasIssue = (field: string) =>
		!validation.success && validation.error.issues.some(issue => issue.path[0] === field);
	const fieldErrors = {
		title: hasIssue("title") ? t("upload.titleInvalid") : undefined,
		description: hasIssue("description") ? t("upload.descriptionLimit", { max: descriptionLimit }) : undefined,
		password: hasIssue("password") ? t("upload.passwordInvalid") : undefined,
	};
	const submitUpload = async () => {
		if (!file || uploading) return;
		if (!validation.success) {
			showUploadFailure("file");
			return;
		}
		setUploading(true);
		setUploadError(undefined);
		cancelledRef.current = false;
		try {
			let fileUrl = uploadedFileRef.current?.file === file ? uploadedFileRef.current.url : undefined;
			if (!fileUrl) {
				const result = await uploadFileChunked(file, {
					title: settings.title,
					description: finalDescription,
					expirationType: settings.expirationType,
				});
				if (result.uncertain) {
					setUploadError({
						message: t("uploadModal.errorUncertain"),
						recovery: t("uploadModal.recoveryUncertainSave"),
						code: result.status ? `HTTP ${result.status}` : undefined,
					});
					return;
				}
				if (result.cancelled || cancelledRef.current) {
					setUploadError({ message: t("uploadModal.cancelled"), tone: "info" });
					return;
				}
				if (!result.success || !result.fileUrl) {
					showUploadFailure(result.category || "unknown", result.status);
					return;
				}
				fileUrl = result.fileUrl;
				uploadedFileRef.current = { file, url: fileUrl };
			}
			setStage("link");
			const order = await createOrder.mutateAsync({
				modelFileUrl: fileUrl,
				title: settings.title.trim(),
				description: finalDescription,
				expirationType: settings.expirationType,
				password: settings.hasPassword ? settings.password : undefined,
			});
			setShortUrl(order.shareId);
		} catch (error) {
			const status =
				error && typeof error === "object" && "data" in error
					? (error.data as { httpStatus?: number } | undefined)?.httpStatus
					: undefined;
			if (uploadedFileRef.current && (!status || status >= 500)) {
				setUploadError({
					message: t("uploadModal.errorUncertain"),
					recovery: t("uploadModal.recoveryUncertainLink"),
					code: status ? `HTTP ${status}` : undefined,
				});
			} else {
				showUploadFailure(navigator.onLine ? uploadFailureCategory(status) : "offline", status);
			}
		} finally {
			setUploading(false);
		}
	};

	const resetWorkspace = () => {
		setFile(null);
		setShortUrl(null);
		setUploadError(undefined);
		setSelectionError(null);
		reset();
		uploadedFileRef.current = null;
	};
	const handleCancelUpload = useCallback(() => {
		cancelledRef.current = true;
		cancelUpload();
	}, [cancelUpload]);

	return {
		shortUrl,
		file,
		uploading: uploading || isChunkedUploading,
		uploadProgress,
		uploadError,
		selectionError,
		descriptionLimit,
		fieldErrors,
		canSubmit: validation.success,
		offline,
		dirty,
		selectFile,
		discardOpen: discardPending || blocker.state === "blocked",
		keepEditing: () => {
			pendingAction.current = null;
			setDiscardPending(false);
			if (blocker.state === "blocked") blocker.reset();
		},
		discard: () => {
			cancelledRef.current = true;
			cancelUpload();
			setDiscardPending(false);
			if (blocker.state === "blocked") blocker.proceed();
			else {
				pendingAction.current?.();
				pendingAction.current = null;
			}
		},
		removeFile: () => requestAction(resetWorkspace),
		fileInputRef,
		handleFileInputChange,
		openFileDialog,
		settings,
		setSettings,
		submitUpload,
		resetWorkspace,
		handleCancelUpload,
	};
}

export type UseCreatePageContext = ReturnType<typeof useIndexPageContext>;
